import {
  Autocomplete,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
} from "@mui/material";
import PersonOutline from "@mui/icons-material/PersonOutline";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { JobRatingLabel } from "../../common/JobOrder";
import {
  useGetTrainingOrderMetadataQuery,
  useGetTrainingOrderInsightQuery,
} from "../../redux/api/trainingOrder";
import { useSelector } from "react-redux";
import { UserGroup } from "../../common/User";
import { selectCurrentUser } from "../../redux/authentication/authSlice";
import { TrainingInsightView } from "../../common/Training";
import { useGetFleetsQuery } from "../../redux/api/fleet";
import { Fleet } from "../../common/Fleet";
import useDocumentTitle from "../../util/useDocumentTitle";
import { Vessel } from "../../common/Vessel";
import { useGetVesselsQuery } from "../../redux/api/vessel";
import { formatDateString } from "../../common/FormatDateString";
import { ProfileDocumentTypeLabel } from '../../common/ProfileDocument';
import moment from 'moment';

const TrainingInsights = () => {
  useDocumentTitle("Training Insights");
  const user = useSelector(selectCurrentUser);
  const isViewOnly =
    (user?.userGroup as UserGroup) === UserGroup.CREWING_EXECS ||
    (user?.userGroup as UserGroup) === UserGroup.FLEET_EXECS ||
    (user?.userGroup as UserGroup) === UserGroup.VESSEL_EXECS;
  const [fleet, setFleet] = useState<Fleet | null>(null);
  const [vessel, setVessel] = useState<Vessel | null>(null);
  const [skipQuery, setSkipQuery] = useState(true);
  const [expiredSorted, setExpiredSorted] = useState<boolean>(true);
  const [expiredSortDirection, setExpiredSortDirection] = useState<string>("asc");
  const [neededSorted, setNeededSorted] = useState<boolean>(true);
  const [neededSortDirection, setNeededSortDirection] = useState<string>("asc");
  const [pendingSorted, setPendingSorted] = useState<boolean>(true);
  const [pendingSortDirection, setPendingSortDirection] = useState<string>("asc");


  const { data: metadata, isLoading: isMetaDataLoading } =
    useGetTrainingOrderMetadataQuery(
      { fleetId: !!vessel ? undefined : fleet?.id, vesselId: vessel?.id },
      {
        refetchOnMountOrArgChange: true,
        skip: skipQuery,
      }
    );
  const { data: fleets, isLoading: isFleetsLoading, isFetching: isFleetsFetching } = useGetFleetsQuery(
    {},
    {
      refetchOnMountOrArgChange: true,
    }
  );
  const { data: vessels, isLoading: isVesselsLoading, isFetching: isVesselsFetching } = useGetVesselsQuery({});

  // Training Insights will either pass fleet or vessel
  const { data: trainingInsightViews, isLoading: isTrainingInsightsLoading } =
    useGetTrainingOrderInsightQuery(
      { fleetId: !!vessel ? undefined : fleet?.id, vesselId: vessel?.id },
      {
        refetchOnMountOrArgChange: true,
        skip: skipQuery,
      }
    );


  const changeFleet = useCallback((fleet: Fleet | null) => {
    if (fleet != null) {
      setVessel(null);
      setFleet(fleet)
      setSkipQuery(false);
    } else {
      setFleet(null);
      setVessel(null);
      setSkipQuery(true);
    }
  }, [setVessel, setFleet, setSkipQuery])

  const changeVessel = (vessel: Vessel | null) => {
    if (vessel != null) {
      setVessel(vessel);
      setSkipQuery(false);
    } else {
      setVessel(null);
      if(!fleet){
        setSkipQuery(true);
      }
    }
  }

  const handleSort = (table: string) => (event: React.MouseEvent<unknown>) => {
    if (table === "expired") {
      const direction =
        expiredSorted === true && expiredSortDirection === "asc"
          ? "desc"
          : "asc";
      setExpiredSorted(true);
      setExpiredSortDirection(direction);
    } else if (table === "needed") {
      const direction =
        neededSorted === true && neededSortDirection === "asc"
          ? "desc"
          : "asc";
      setNeededSorted(true);
      setNeededSortDirection(direction);
    } else {
      const direction =
        pendingSorted === true && pendingSortDirection === "asc"
          ? "desc"
          : "asc";
      setPendingSorted(true);
      setPendingSortDirection(direction);
    }
  }

  const getVesselsList = () => {
    if(!!fleet){
      return fleet.vessels.filter(v => !v.disabled);
    }
    else{
      return vessels ? (vessels as Vessel[]).filter(v => !v.disabled) : [];
    }
  }

  useEffect(() => {
    if (fleets?.length === 1) {
      changeFleet(fleets[0]);
    }
  }, [fleets, changeFleet]);

  const createTableBody = (data: TrainingInsightView[], expirationDateSorted: boolean, expirationDateDirection: string, highlightExpired: boolean = false): JSX.Element => {
    const sortedData = [...data];
    if (expirationDateSorted) {
      sortedData.sort((a, b) => {
        if (expirationDateDirection === "asc") {
          return new Date(a.expirationDate) >= new Date(b.expirationDate) ? 1 : -1
        } else {
          return new Date(a.expirationDate) < new Date(b.expirationDate) ? 1 : -1
        }
      })
    }
    return (
      <TableBody>
        {sortedData.map((row) => {
          return (
            <TableRow key={`${row.profile.id} ${row.training !== null ? row.training.name : 'null-training'}`}>
              <TableCell className="rating left wb-wrap">
                <div className="rating-inner">
                  <Tooltip
                    title={
                      row.profile.rating !== undefined
                        ? JobRatingLabel[row.profile.rating]
                        : ""
                    }
                    placement="right"
                  >
                    <span>
                      {row.profile.rating !== undefined
                        ? row.profile.rating.substring(1)
                        : ""}
                    </span>
                  </Tooltip>
                </div>
              </TableCell>
              <TableCell>
                {isViewOnly ? (
                  <p style={{ textAlign: "left" }}>
                    {row.profile.firstName} {row.profile.middleName}{" "}
                    {row.profile.lastName}
                  </p>
                ) : (
                  <Link
                    style={{ justifyContent: "start" }}
                    className="blue-link"
                    to={`/profiles/${row.profile.id}`}
                  >
                    {row.profile.firstName} {row.profile.middleName}{" "}
                    {row.profile.lastName}
                  </Link>
                )}
              </TableCell>
              <TableCell>
                <Checkbox
                  name="onboarded"
                  checked={row.onboard}
                  disabled={true}
                />
              </TableCell>
              {row.training !== null ? (
                <TableCell>{row.training.name || ''}</TableCell>
              ) : row.documentType !== null ? (
                <TableCell>{ProfileDocumentTypeLabel[row.documentType]}</TableCell>
              ) : (
                <TableCell></TableCell>
              )}
              <TableCell style={{
                color: highlightExpired && moment(row.expirationDate).isBefore(moment()) ? 'red' : undefined
              }}>
                {formatDateString(row.expirationDate?.toString())}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    );
  };

  return (
    <div>
      <div className="flex-col wd-100">
        <div className="flex align-items-center justify-content-btwn mb-1">
          <h1>Training Insights</h1>
        </div>
        <div className="filters-wrap">
          <div className="filters-list">
            {!isFleetsLoading && !isFleetsFetching && (
              <label
                className="large-filter"
                style={{ width: "200px", marginTop: "30px" }}
              >
                <span>Fleet:</span>
                <Autocomplete
                  className="fleet-dropdown"
                  options={fleets ? (fleets as Fleet[]) : []}
                  value={fleet}
                  onChange={(event, value) => {
                    changeFleet(value)
                  }}
                  renderInput={(params) => <TextField {...params} />}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  getOptionLabel={(opt) => (opt.name ? opt.name : "")}
                />
              </label>
            )}
            { !isFleetsLoading && !isFleetsFetching &&
              !isVesselsLoading && !isVesselsFetching && (
              <label
                className="large-filter"
                style={{ width: "200px", marginTop: "30px" }}
              >
                <span>Vessel:</span>
                <Autocomplete
                  style={{ width: "100px" }}
                  className="vessel-dropdown"
                  id="vessels"
                  value={vessel ? vessel : null}
                  options={getVesselsList()}
                  getOptionLabel={(opt) => (opt.name ? opt.name : "")}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderInput={(params) => (
                    <TextField {...params} />
                  )}
                  onChange={(e, val) => changeVessel(val)}
                />
              </label>

            )}
          </div>
        </div>
        {!isMetaDataLoading && metadata != null && (
          <div className="rollup" style={{ marginTop: "3%" }}>
            <div className="statistic p-2 flex-col justify-content-center gr-0 bg-lightred">
              <div className="flex g-1">
                <PersonOutline className="color-red" />
                <h2>{metadata?.expiredCount}</h2>
              </div>
              <p>Trainings Expired</p>
            </div>
            <div className="statistic p-2 flex-col justify-content-center gr-0 bg-lightblue">
              <div className="flex g-1">
                <PersonOutline className="color-blue" />
                <h2>{metadata?.trainingNeededCount}</h2>
              </div>
              <p>Trainings Needed</p>
            </div>
            <div className="statistic p-2 flex-col justify-content-center gr-0 bg-lightorange">
              <div className="flex g-1">
                <PersonOutline className="color-orange" />
                <h2>{metadata?.pendingConfirmationCount}</h2>
              </div>
              <p>Pending Confirmation</p>
            </div>
            <div className="statistic p-2 flex-col justify-content-center gr-0 bg-lightgreen">
              <div className="flex g-1">
                <PersonOutline className="color-green" />
                <h2>{metadata?.documentsExpiredCount}</h2>
              </div>
              <p>Documents Expired</p>
            </div>
          </div>
        )}
        {!!trainingInsightViews && !isTrainingInsightsLoading != null && (
          <>
            <h2 style={{ marginTop: "5%" }}>Document Tracking</h2>
            <div
              style={{
                height: "2px",
                width: "100%",
                backgroundColor: "#408b00",
              }}
            />
            {trainingInsightViews.documentInsights.length === 0 ? (
              <h4 style={{ marginTop: "1%" }}>
                No documents expired nor expiring within 9 months.
              </h4>
            ) : (
              <Table className="table" style={{ width: "100%" }}>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: "10%" }}>Rating</TableCell>
                    <TableCell style={{ width: "30%", textAlign: "left" }}>
                      Mariner Name
                    </TableCell>
                    <TableCell style={{ width: "10%" }}>Onboard</TableCell>
                    <TableCell style={{ width: "30%" }}>Document Type</TableCell>
                    <TableCell style={{ width: "20%" }}>
                      <TableSortLabel
                        className="flex align-items-center"
                        active={expiredSorted}
                        direction={expiredSortDirection === "asc" ? "asc" : "desc"}
                        onClick={handleSort("expired")}
                      >
                        Expiration Date
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                {createTableBody(trainingInsightViews?.documentInsights, expiredSorted, expiredSortDirection, true)}
              </Table>
            )}
            <h2 style={{ marginTop: "5%" }}>Expired Mariner Trainings</h2>
            <div
              style={{
                height: "2px",
                width: "100%",
                backgroundColor: "#fa4343",
              }}
            />
            {trainingInsightViews.expiredTrainingInsights.length === 0 ? (
              <h4 style={{ marginTop: "1%" }}>
                No Expired Mariner Trainings to display.
              </h4>
            ) : (
              <Table className="table" style={{ width: "100%" }}>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: "10%" }}>Rating</TableCell>
                    <TableCell style={{ width: "30%", textAlign: "left" }}>
                      Mariner Name
                    </TableCell>
                    <TableCell style={{ width: "10%" }}>Onboard</TableCell>
                    <TableCell style={{ width: "30%" }}>Training</TableCell>
                    <TableCell style={{ width: "20%" }}>
                      <TableSortLabel
                        className="flex align-items-center"
                        active={expiredSorted}
                        direction={expiredSortDirection === "asc" ? "asc" : "desc"}
                        onClick={handleSort("expired")}
                      >
                        Expiration Date
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                {createTableBody(trainingInsightViews?.expiredTrainingInsights, expiredSorted, expiredSortDirection)}
              </Table>
            )}

            <h2 style={{ marginTop: "5%" }}>Mariner Trainings Needed</h2>
            <div
              style={{
                height: "2px",
                width: "100%",
                backgroundColor: "#4352fa",
              }}
            />
            {trainingInsightViews.neededTrainingInsights.length === 0 ? (
              <h4 style={{ marginTop: "1%" }}>
                No Mariner Trainings Needed to display.
              </h4>
            ) : (
              <Table className="table" style={{ width: "100%" }}>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: "10%" }}>Rating</TableCell>
                    <TableCell style={{ width: "30%", textAlign: "left" }}>
                      Mariner Name
                    </TableCell>
                    <TableCell style={{ width: "10%" }}>Onboard</TableCell>
                    <TableCell style={{ width: "30%" }}>Training</TableCell>
                    <TableCell style={{ width: "20%" }}>
                      <TableSortLabel
                        className="flex align-items-center"
                        active={neededSorted}
                        direction={neededSortDirection === "asc" ? "asc" : "desc"}
                        onClick={handleSort("needed")}
                      >
                        Expiration Date
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                {createTableBody(trainingInsightViews?.neededTrainingInsights, neededSorted, neededSortDirection)}
              </Table>
            )}

            <h2 style={{ marginTop: "5%" }}>Pending Training Confirmation</h2>
            <div
              style={{
                height: "2px",
                width: "100%",
                backgroundColor: "#fa8c43",
              }}
            />
            {trainingInsightViews.pendingTrainingInsights.length === 0 ? (
              <h4 style={{ marginTop: "1%" }}>
                No Trainings Pending Confirmation to display.
              </h4>
            ) : (
              <Table className="table" style={{ width: "100%" }}>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: "10%" }}>Rating</TableCell>
                    <TableCell style={{ width: "30%", textAlign: "left" }}>
                      Mariner Name
                    </TableCell>
                    <TableCell style={{ width: "10%" }}>Onboard</TableCell>
                    <TableCell style={{ width: "30%" }}>Training</TableCell>
                    <TableCell style={{ width: "20%" }}>
                      <TableSortLabel
                        className="flex align-items-center"
                        active={pendingSorted}
                        direction={pendingSortDirection === "asc" ? "asc" : "desc"}
                        onClick={handleSort("pending")}
                      >
                        Expiration Date
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                {createTableBody(trainingInsightViews?.pendingTrainingInsights, pendingSorted, pendingSortDirection)}
              </Table>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TrainingInsights;
